import UserInitials from '@components/UserInitials'
import ArrowLargeIcon from '@images/icons/arrow-expense-large.svg?react'
import ArrowShortIcon from '@images/icons/arrow-expense-short.svg?react'
import CheckIcon from '@images/icons/check-activity.svg?react'
import ExpenseMoneyIcon from '@images/icons/expense-money-icon.svg?react'
import ExpenseSubmittedIcon from '@images/icons/expense-payment-started.svg?react'
import type { ReportResponse } from 'mmfintech-backend-api/types'
import { isValidArray, tr } from 'mmfintech-commons'
import React, { useMemo } from 'react'
import { findApproverByName, findApproverByRole } from './ApprovalStatus.helper'

import './approvalStatus.scss'
import cn from 'classnames'
import { ExpenseStatuses, ReportStates } from 'mmfintech-backend-api'

const NO_MATRIX_FLOW_DATA = [
  {
    index: 1,
    firstName: 'Approver',
    customerRole: 'Any'
  },
  {
    index: 2,
    firstName: 'Final',
    lastName: 'Approver',
    customerRole: 'Any'
  }
]

type ApprovalStatusProps = {
  report?: ReportResponse
}

const ApprovalStatus = ({ report }: ApprovalStatusProps) => {
  const { approvalStatus, state, submitter } = report || {}

  const { hasMatrix, currentPriority, submitted, approvalLevelApprovers, processed, lastApprover } =
    approvalStatus || {}

  const transformedData = useMemo(
    () => (approvalLevelApprovers ? Object.keys(approvalLevelApprovers).map(key => approvalLevelApprovers[key]) : []),
    [approvalLevelApprovers]
  )

  const isPaymentStarted = useMemo(
    () =>
      report
        ? report?.approvalStatus?.processed &&
          report?.state === ReportStates.APPROVED &&
          report?.expenses?.some(expense => expense?.status !== ExpenseStatuses.NOT_PAID)
        : false,
    [report]
  )

  const isReportRejected = useMemo(() => report?.state === ReportStates.REJECTED, [report?.state])

  const approverNoMatrix = useMemo(() => {
    return findApproverByRole('STAFF', approvalLevelApprovers)
  }, [approvalLevelApprovers])

  const finalApproverNoMatrix = useMemo(() => {
    return findApproverByRole(['OWNER', 'ADMINISTRATOR'], approvalLevelApprovers)
  }, [approvalLevelApprovers])

  const isFinalApproverOutOfMatrix = useMemo(
    () =>
      (report?.approvalStatus?.processed || report?.state === ReportStates.COMPLETELY_PAID) &&
      !findApproverByName(report?.approvalStatus?.lastApprover, report?.approvalStatus?.approvalLevelApprovers),
    [report]
  )

  return report ? (
    <div className='matrix-approvers'>
      <UserInitials
        user={{ firstName: submitter }}
        isSubmitted={submitted}
        approverAction={state === ReportStates.EDIT ? 'EDIT' : null}
      />

      <ArrowLargeIcon />

      {!hasMatrix
        ? NO_MATRIX_FLOW_DATA.map((approver, index) => {
            const isSkipped =
              (processed &&
                (index === 0 ? !approverNoMatrix?.approverAction : !finalApproverNoMatrix?.approverAction)) ||
              (index === 0 && !approverNoMatrix?.approverAction && finalApproverNoMatrix?.approverAction)
            return (
              <React.Fragment key={`no-matrix-${approver.index}`}>
                <UserInitials
                  user={index === 0 ? approverNoMatrix || approver : finalApproverNoMatrix || approver}
                  approverAction={
                    isSkipped
                      ? tr('FRONTEND.APPROVAL_STATUS.SKIPPED', 'SKIPPED')
                      : index === 0
                        ? approverNoMatrix?.approverAction
                        : finalApproverNoMatrix?.approverAction
                  }
                  skipped={
                    isReportRejected &&
                    (index === 0 ? !approverNoMatrix?.approverAction : !finalApproverNoMatrix?.approverAction)
                  }
                />

                {NO_MATRIX_FLOW_DATA.length === index + 1 ? (
                  <ArrowLargeIcon width={24} />
                ) : (
                  <ArrowShortIcon width={8} />
                )}
              </React.Fragment>
            )
          })
        : transformedData.map((approvalLevel, index) => {
            const filteredApprovalLevel = Array.isArray(approvalLevel)
              ? approvalLevel.filter(approver => approver.approverAction)
              : []

            if (isValidArray(filteredApprovalLevel)) {
              const approver = filteredApprovalLevel[0]
              return (
                <React.Fragment key={approver.userId}>
                  <UserInitials
                    user={approver}
                    className={`user-initials`}
                    approverAction={approver.approverAction}
                    skipped={isReportRejected && !approver.approverAction}
                  />
                  <ArrowShortIcon width={8} />
                </React.Fragment>
              )
            }

            return approvalLevel.map((approver, approverIndex) => {
              const isSkipped =
                (ReportStates.APPROVED === state || ReportStates.COMPLETELY_PAID === state) &&
                approver.priority < currentPriority &&
                !approver?.approverAction

              return (
                <React.Fragment key={approver.userId}>
                  {(approverIndex === 0 || approvalLevel.length === 2) && (
                    <UserInitials
                      user={approver}
                      className={cn('user-initials', approverIndex > 0 ? `user-${index}` : undefined)}
                      approverAction={
                        isSkipped ? tr('FRONTEND.APPROVAL_STATUS.SKIPPED', 'SKIPPED') : approver.approverAction
                      }
                      skipped={isReportRejected && !approver.approverAction}
                    />
                  )}

                  {approverIndex === 1 && approvalLevel.length > 2 && (
                    <UserInitials
                      users={approvalLevel.slice(1)}
                      className={cn('user-initials', approverIndex > 0 ? `user-${index}` : undefined)}
                      approverAction={
                        isSkipped ? tr('FRONTEND.APPROVAL_STATUS.SKIPPED', 'SKIPPED') : approver.approverAction
                      }
                      skipped={isReportRejected && !approver.approverAction}
                      label={`+${approvalLevel.length - approverIndex}`}
                    />
                  )}

                  {approvalLevel.length === approverIndex + 1 ? (
                    transformedData.length === index + 1 ? (
                      <ArrowLargeIcon width={24} />
                    ) : (
                      <ArrowShortIcon width={8} />
                    )
                  ) : null}
                </React.Fragment>
              )
            })
          })}

      {isFinalApproverOutOfMatrix && hasMatrix && (
        <>
          <UserInitials
            user={{
              firstName: lastApprover,
              customerRole: tr('FRONTEND.EM.APPROVAL_STATUS.SKIPPED_BY_ADMIN', 'Skipped by Admin')
            }}
            className='user-initials'
            approverAction='APPROVE'
          />
          <ArrowLargeIcon width={24} />
        </>
      )}

      <div className={cn('final-matrix-step', { skipped: isReportRejected })}>
        <ExpenseMoneyIcon />
        {report?.state === ReportStates.COMPLETELY_PAID && (
          <div className='success-icon'>
            <CheckIcon width={16} height={16} />
          </div>
        )}
        {isPaymentStarted && (
          <div className='success-icon'>
            <ExpenseSubmittedIcon width={16} height={16} />
          </div>
        )}
      </div>
    </div>
  ) : null
}

export default ApprovalStatus
